import { IonIcon } from '@ionic/react';
import { scheduleScrollFocusedInputIntoView } from '@lidea/shared/util/core';
import { TextInput } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import classNames from 'classnames';

import styles from './pf-input.module.css';

export interface PfInputProps {
  label?: string;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (value: string | number) => void;
  error?: string;
  /** @deprecated Use `error` — kept for existing forms */
  errorText?: string;
  disabled?: boolean;
  type?: React.HTMLInputTypeAttribute;
  wrapperClassName?: string;
  ariaLabel?: string;
  className?: string;
  icon?: string;
  name?: string;
  readOnly?: boolean;
  /** @deprecated Use `readOnly` */
  readonly?: boolean;
  /** Legacy Ionic props — ignored, styling matches PfSelect */
  fill?: string;
  labelPlacement?: string;
  isTouched?: boolean;
  height?: 'small' | 'large';
  color?: string;
  scrollOnFocus?: boolean;
}

export function PfInput({
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  error,
  errorText,
  disabled,
  type = 'text',
  wrapperClassName,
  ariaLabel,
  className,
  icon,
  name,
  readOnly,
  readonly,
  scrollOnFocus,
}: PfInputProps) {
  const resolvedError = error ?? errorText;

  const handleFocus = () => {
    if (scrollOnFocus) {
      scheduleScrollFocusedInputIntoView({ behavior: 'smooth' });
    }
  };

  const [_value, handleChange] = useUncontrolled({
    value,
    defaultValue: defaultValue as typeof value,
    finalValue: '',
    onChange,
  });

  return (
    <div className={classNames(styles.field, wrapperClassName)}>
      <TextInput
        aria-label={ariaLabel ?? label}
        className={classNames(styles.input, icon && styles.inputWithIcon, className)}
        disabled={disabled}
        error={resolvedError}
        label={label}
        name={name}
        readOnly={readOnly ?? readonly}
        leftSection={
          icon ? (
            <span className={styles.iconSection}>
              <IonIcon aria-hidden icon={icon} />
            </span>
          ) : undefined
        }
        placeholder={placeholder}
        type={type}
        value={_value != null ? String(_value) : ''}
        onChange={(event) => handleChange(event.currentTarget.value)}
        onFocus={handleFocus}
      />
    </div>
  );
}

export default PfInput;
